import React, { useMemo } from "react";
import {
  FiAlertTriangle as AlertTriangle,
  FiClock as Clock,
  FiDroplet as Droplets,
  FiActivity as FlaskConical,
  FiDatabase as Database,
} from "react-icons/fi";
import "./MetricCards.css";

const getMetricConfig = () => [
  {
    key: "cip_rate",
    label: "Flagged Runs",
    icon: Database,
    gradient: "linear-gradient(135deg, #48cc6c 0%, #2dd57a 100%)",
    iconColor: "#2dd57a",
    shadowColor: "rgba(72, 204, 108, 0.3)",
  },
  {
    key: "alerts",
    label: "Alerts",
    icon: AlertTriangle,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    iconColor: "#667eea",
    shadowColor: "rgba(102, 126, 234, 0.3)",
  },
  {
    key: "time_waste_s",
    label: "Excessive Time",
    icon: Clock,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    iconColor: "#f5576c",
    shadowColor: "rgba(245, 87, 108, 0.3)",
  },
  {
    key: "water_waste_l",
    label: "Water Loss",
    icon: Droplets,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    iconColor: "#00f2fe",
    shadowColor: "rgba(0, 242, 254, 0.3)",
  },
  {
    key: "chemical_waste_l",
    label: "Chemical Loss",
    icon: FlaskConical,
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    iconColor: "#fa709a",
    shadowColor: "rgba(250, 112, 154, 0.3)",
  },
];

export default function MetricCards({
  totalAlerts,
  totalTimeWaste,
  totalWaterWaste,
  totalChemicalWaste,
  cipMetrics = { defectedCips: 0, totalCips: 0, defectRate: 0 },
  convertTime,
  convertVolume,
  getVolumeUnit,
  getTimeUnit,
  selectedMetric = null,
  onMetricSelect = null,
  showSelection = false,
}) {
  const processedMetrics = useMemo(() => {
    const metricConfigs = getMetricConfig();

    return metricConfigs.map((config) => ({
      ...config,
      value: (() => {
        switch (config.key) {
          case "cip_rate":
            return `${cipMetrics.defectedCips} of ${cipMetrics.totalCips}`;
          case "alerts":
            return totalAlerts.toLocaleString();
          case "time_waste_s":
            return convertTime(totalTimeWaste);
          case "water_waste_l":
            return convertVolume(totalWaterWaste).toLocaleString();
          case "chemical_waste_l":
            return convertVolume(totalChemicalWaste).toLocaleString();
          default:
            return "";
        }
      })(),
      unit: (() => {
        switch (config.key) {
          case "cip_rate":
            return "";
          case "alerts":
            return "";
          case "time_waste_s":
            return getTimeUnit();
          case "water_waste_l":
          case "chemical_waste_l":
            return getVolumeUnit();
          default:
            return "";
        }
      })(),
    }));
  }, [
    totalAlerts,
    totalTimeWaste,
    totalWaterWaste,
    totalChemicalWaste,
    cipMetrics,
    convertTime,
    convertVolume,
    getTimeUnit,
    getVolumeUnit,
  ]);

  const handleMouseEnter = (e, isClickable, isSelected) => {
    if (isClickable && !isSelected) {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 6px 24px rgba(0, 0, 0, 0.15)";
    }
  };

  const handleMouseLeave = (e, isClickable, isSelected) => {
    if (isClickable && !isSelected) {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.1)";
    }
  };

  return (
    <div className="metric-cards-container">
      {processedMetrics.map((metric) => {
        const Icon = metric.icon;
        const isSelected = showSelection && selectedMetric === metric.key;
        const isClickable = showSelection && onMetricSelect;

        return (
          <div
            key={metric.key}
            onClick={isClickable ? () => onMetricSelect(metric.key) : undefined}
            className={`metric-card ${isClickable ? "clickable" : ""} ${
              isSelected ? "selected" : ""
            }`}
            style={{
              background: isSelected ? metric.gradient : undefined,
              boxShadow: isSelected
                ? `0 8px 32px ${metric.shadowColor}`
                : undefined,
            }}
            onMouseEnter={(e) => handleMouseEnter(e, isClickable, isSelected)}
            onMouseLeave={(e) => handleMouseLeave(e, isClickable, isSelected)}
          >
            {/* Background pattern */}
            <div
              className="metric-card-background-pattern"
              style={{
                background: isSelected
                  ? "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)"
                  : `radial-gradient(circle, ${metric.iconColor}15 0%, transparent 70%)`,
              }}
            />

            <div className="metric-card-header">
              <div
                className={`metric-card-icon-container ${
                  isSelected ? "selected" : ""
                }`}
                style={{
                  background: !isSelected
                    ? `linear-gradient(135deg, ${metric.iconColor}20, ${metric.iconColor}10)`
                    : undefined,
                }}
              >
                <Icon
                  size={24}
                  className={`metric-card-icon ${isSelected ? "selected" : ""}`}
                  style={{
                    color: !isSelected ? metric.iconColor : undefined,
                  }}
                />
              </div>
              {isSelected && (
                <div className="metric-card-selection-indicator" />
              )}
            </div>

            {/* Center-aligned value container */}
            <div className="metric-card-value-container">
              <div
                className={`metric-card-value ${isSelected ? "selected" : ""}`}
              >
                {metric.value}
                <span className="metric-card-unit">{metric.unit}</span>
              </div>
            </div>

            <div
              className={`metric-card-label ${isSelected ? "selected" : ""}`}
            >
              {metric.label}
            </div>

            {/* Subtle animation indicator */}
            {isSelected && <div className="metric-card-animation-indicator" />}
          </div>
        );
      })}
    </div>
  );
}
